import { useState, useCallback } from 'react';
import { profitApi } from '../api/profit';
import type { ProfitSnapshot, GenerateProfitPayload } from '../api/profit';

export function useProfit() {
  const [snapshots, setSnapshots] = useState<ProfitSnapshot[]>([]);
  const [activeSnapshot, setActiveSnapshot] = useState<ProfitSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchSnapshots = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await profitApi.list(p);
      setSnapshots(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load profit snapshots.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GenerateProfitPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await profitApi.generate(payload);
      const snapshot = res.data.data;
      setActiveSnapshot(snapshot);
      setSnapshots((prev) => [snapshot, ...prev]);
      return snapshot;
    } catch (err: any) {
      const msg = err?.message ?? 'Profit insight generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteSnapshot = useCallback(async (id: number) => {
    try {
      await profitApi.destroy(id);
      setSnapshots((prev) => prev.filter((s) => s.id !== id));
      if (activeSnapshot?.id === id) setActiveSnapshot(null);
    } catch {
      setError('Failed to delete snapshot.');
    }
  }, [activeSnapshot]);

  return {
    snapshots, activeSnapshot, loading, generating, error, total, page, lastPage,
    fetchSnapshots, generate, deleteSnapshot, setActiveSnapshot, setError,
  };
}
